// ===============================================================
// VerveAI Startup Launchpad — Packages & Discount Configuration
// Default Promotion: 50% Launch Discount
// ===============================================================

export const DEFAULT_DISCOUNT_PERCENT = 50;
export const DEFAULT_COUPON_CODE = 'LAUNCH50';

export const VALID_COUPONS = {
  LAUNCH50: {
    code: 'LAUNCH50',
    discountPercent: 50,
    label: '50% Turnkey Launchpad Special',
    description: 'Flat 50% discount for newly incorporated companies & startups',
  },
  STARTUP50: {
    code: 'STARTUP50',
    discountPercent: 50,
    label: 'Founder Inaugural Discount',
    description: '50% off digital infrastructure setup',
  },
  VERVE50: {
    code: 'VERVE50',
    discountPercent: 50,
    label: 'Verve AI Labs Ecosystem Benefit',
    description: '50% grant for AI-first startup launch',
  },
};

export const LAUNCHPAD_PACKAGES = [
  {
    id: 'starter',
    name: 'Starter Launchpad',
    displayName: '₹4,999 Startup Launchpad (48–72 Hrs)',
    subtitle: 'Complete Day-1 digital foundations engineered for new founders—delivered in 48–72 hours.',
    originalPrice: 9999,
    billingText: '/ one-time',
    turnaround: '48–72 Hours Guaranteed',
    isPopular: false,
    badgeText: 'INAUGURAL OFFER • 50% OFF',
    features: [
      '1 Official Brand Domain (.in or .com) + DNS',
      'High-Speed 5-Page Web Storefront (Sub-second load)',
      '1 Custom Domain Corporate Email Account',
      'Cryptographic SPF, DKIM & DMARC DNS Security',
      '24/7 AI Receptionist (WhatsApp Lead Routing)',
      'Turnaround: 48–72 Hours Guaranteed',
    ],
    ctaText: 'Claim ₹4,999 Launchpad',
    whatsappMessage: 'Hi VerveAI! I want to claim the ₹4,999 Startup Launchpad (48-72 Hrs) with inaugural code LAUNCH50.',
  },
  {
    id: 'mvp',
    name: '2–4 Week MVP Development',
    displayName: '2–4 Week MVP Development (Custom AI & Full-Stack)',
    subtitle: 'Stop battling fragile Zapier spaghetti. We build scalable custom AI tools, workflows, and production MVPs.',
    originalPrice: null, // Custom quote
    billingText: 'Sprint Scope',
    turnaround: '2–4 Weeks (14–28 Days)',
    isPopular: true,
    badgeText: 'RAPID PRODUCT BUILD • ZERO TECH DEBT',
    features: [
      'Full-Stack Web or Mobile Application (Next.js, React, Node)',
      'Custom AI Agents & Multi-Step Logic Automation',
      'Production Vector RAG & LLM Reasoning Pipelines',
      'Database Architecture, Auth & Payments (Stripe/Razorpay)',
      '100% In-House Intellectual Property & Zero Lock-in',
      'Turnaround: 2–4 Weeks Agile Sprints',
    ],
    ctaText: 'Build Your MVP (2–4 Wks)',
    whatsappMessage: "Hi VerveAI! I want to discuss building a 2-4 Week MVP / Custom AI Workflow for my startup.",
  },
  {
    id: 'growth',
    name: 'Growth Enterprise Launchpad',
    displayName: 'Growth Enterprise Launchpad',
    subtitle: 'Complete multi-pillar digital footprint for newly registered Pvt Ltds & LLPs.',
    originalPrice: 19999,
    billingText: '/ one-time',
    turnaround: '48 Hours Guaranteed',
    isPopular: false,
    badgeText: 'COMPLETE MULTI-PILLAR SUITE',
    features: [
      'Complete Brand Domain (.in + .com + .co.in)',
      'Multi-Page High-Converting Web Storefront',
      'Custom Domain Business Email (Up to 5 team mailboxes)',
      'Full Cryptographic SPF, DKIM, DMARC Suite',
      '24/7 AI Customer Receptionist Agent',
      'Instant WhatsApp Lead Routing to Founders',
      'Google Business Profile & Map Listing',
      'Turnaround: 48 Hours Guaranteed',
    ],
    ctaText: 'Select Growth Launchpad',
    whatsappMessage: 'Hi VerveAI! I want to select the Growth Enterprise Launchpad with code LAUNCH50.',
  },
  {
    id: 'custom',
    name: 'Custom AI & Deep Tech',
    displayName: 'Enterprise AI Swarms & Custom MCP',
    subtitle: 'Direct engagement with Verve AI Labs parent studio for enterprise swarms & private cloud.',
    originalPrice: null, // Custom quote
    billingText: 'Enterprise Scope',
    turnaround: 'Tailored Timeline',
    isPopular: false,
    badgeText: 'PARENT STUDIO RESEARCH & ENG',
    features: [
      'Everything in 2–4 Week MVP',
      'Multi-Agent Reasoning Swarms & Custom MCP Servers',
      'In-House SFT & RLHF Model Alignment',
      'Private Vector Database (RAG) Architecture',
      'Private Cloud / On-Premise VPC Isolation',
      'Senior AI Architects Consultation',
    ],
    ctaText: 'Consult Verve AI Labs Studio',
    externalLink: 'https://verveailabs.com',
    whatsappMessage: 'Hi Verve AI Labs! We are looking for enterprise-grade Agentic Swarms, MCP Servers, or Private Cloud AI.',
  },
];

/**
 * Calculates discount details given an original price and coupon code (or default discount)
 */
export function calculatePricing(originalPrice, couponCode = DEFAULT_COUPON_CODE) {
  if (originalPrice === null || originalPrice === undefined) {
    return {
      originalPrice: null,
      finalPrice: null,
      discountAmount: 0,
      discountPercent: 0,
      couponCode: null,
      isValidCoupon: false,
      couponLabel: null,
      formattedOriginalPrice: 'Custom',
      formattedFinalPrice: 'Custom',
      formattedSavings: null,
    };
  }

  const normalizedCode = (couponCode || '').trim().toUpperCase();
  const coupon = VALID_COUPONS[normalizedCode];

  const discountPercent = coupon ? coupon.discountPercent : DEFAULT_DISCOUNT_PERCENT;
  const isValidCoupon = Boolean(coupon) || normalizedCode === DEFAULT_COUPON_CODE;

  // Calculate final discounted amount (rounding to clean currency format: ₹9,999 -> ₹4,999; ₹19,999 -> ₹9,999)
  const finalPrice = Math.floor(originalPrice * (1 - discountPercent / 100));
  const discountAmount = originalPrice - finalPrice;

  return {
    originalPrice,
    finalPrice,
    discountAmount,
    discountPercent,
    couponCode: normalizedCode || DEFAULT_COUPON_CODE,
    isValidCoupon,
    couponLabel: coupon ? coupon.label : `${discountPercent}% Launch Promotion`,
    formattedOriginalPrice: `₹${originalPrice.toLocaleString('en-IN')}`,
    formattedFinalPrice: `₹${finalPrice.toLocaleString('en-IN')}`,
    formattedSavings: `Save ₹${discountAmount.toLocaleString('en-IN')}`,
  };
}
